import React, { useState, useMemo, useEffect } from 'react'
import { startOfISOWeek, subWeeks, addDays, differenceInDays } from 'date-fns';
import UserRoleTable from './UserRoleTable';
import UserRoleTableView from './UserRoleTableView';
import DashboardTable from '../DashboardTable/Index';
import DashboardFilters from '../DashboardFilters/Index';
import UserRoleTopHeader from './UserRoleTopHeader';
import UserRoleDetailPopup from './UserRoleDetailPopup';
import ProductLinePopup from './ProductLinePopup';
import { useDataContext } from '../../../hooks/useDataContext';

const getDayName = (date) => {
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return daysOfWeek[date.getDay()];
};

const getMonthName = (date) => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return months[date.getMonth()];
};

// Session storage key for selected view (resets on page reload)
const SELECTED_VIEW_SESSION_KEY = 'userRole_selectedView_session';

const UserRoleLayout = ({ UserRoleData = [], officePartnerData = null }) => {
  console.log('UserRoleLayout component rendered');
  console.log('officePartnerData', officePartnerData);
  
  const { fetchData, dispatch, filters } = useDataContext();
  
  // Initialize from sessionStorage if available, otherwise default to 'task' on first load
  const [selectedView, setSelectedView] = useState(() => {
    const savedView = sessionStorage.getItem(SELECTED_VIEW_SESSION_KEY);
    // On first load, if no saved view exists, default to 'task'
    return savedView === 'enquiry' || savedView === 'task' ? savedView : 'task';
  });
  
  // Save to sessionStorage whenever selectedView changes (persists during session, resets on page reload)
  useEffect(() => {
    sessionStorage.setItem(SELECTED_VIEW_SESSION_KEY, selectedView);
  }, [selectedView]);
  const [isCalendarClicked, setIsCalendarClicked] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isProductLinePopupOpen, setIsProductLinePopupOpen] = useState(false);

  // Transform officePartnerData to match table format and filter by selected view
  const transformedData = useMemo(() => {
    console.log('Transforming officePartnerData:', officePartnerData, 'selectedView:', selectedView);
    
    if (!officePartnerData) {
      console.log('officePartnerData is falsy');
      return null;
    }

    let dataToTransform = [];

    // Check if it's the API response structure (full response: { status, data: { tasks: { assigned: { tasks: [], inquiry: [] } } } })
    if (officePartnerData.data && officePartnerData.data.tasks && officePartnerData.data.tasks.assigned) {
      const { inquiry = [], tasks = [] } = officePartnerData.data.tasks.assigned;
      console.log('Found API response structure - inquiry:', inquiry.length, 'tasks:', tasks.length);
      
      // Filter based on selected view
      if (selectedView === 'enquiry') {
        dataToTransform = inquiry;
      } else {
        dataToTransform = tasks; // Default to tasks
      }
    }
    // Check if it's the assigned object directly (from context: response.data.tasks.assigned = { tasks: [], inquiry: [] })
    else if (officePartnerData.inquiry !== undefined || officePartnerData.tasks !== undefined) {
      const { inquiry = [], tasks = [] } = officePartnerData;
      console.log('Found assigned object directly - inquiry:', inquiry.length, 'tasks:', tasks.length);
      
      // Filter based on selected view
      if (selectedView === 'enquiry') {
        dataToTransform = inquiry;
      } else {
        dataToTransform = tasks; // Default to tasks
      }
    }
    // Check if it's already an array
    else if (Array.isArray(officePartnerData)) {
      console.log('officePartnerData is already an array');
      dataToTransform = officePartnerData;
    }
    else {
      console.log('officePartnerData structure not recognized:', officePartnerData);
      return null;
    }

    if (dataToTransform.length === 0) {
      console.log('No data found for selected view:', selectedView);
      return null;
    }

    const transformed = dataToTransform.map((item, index) => ({
      id: item.id || index + 1,
      title: item.title || '',
      assignedBy: item.assignedBy || '',
      executionDate: item.startDate || item.executionDate || '',
      deadlineDate: item.deadlineDate || item.endDate || '',
      ...item // Include all other properties from the original item
    }));
    
    console.log('Transformed data for', selectedView, ':', transformed);
    return transformed;
  }, [officePartnerData, selectedView]);

  // Use transformedData if available, otherwise fallback to UserRoleData
  const tableData = transformedData || UserRoleData;
  console.log('Final tableData:', tableData);

  // Extract products from officePartnerData
  const productsData = useMemo(() => {
    if (!officePartnerData) {
      console.log('UserRoleLayout - officePartnerData is null/undefined');
      return [];
    }

    console.log('UserRoleLayout - officePartnerData structure:', officePartnerData);

    // Check if it's the full API response structure
    if (officePartnerData.data) {
      // Check in data.products (top level)
      if (officePartnerData.data.products) {
        console.log('UserRoleLayout - Found products in data.products:', officePartnerData.data.products);
        return Array.isArray(officePartnerData.data.products) ? officePartnerData.data.products : [];
      }
      // Check in data.tasks.assigned.products
      if (officePartnerData.data.tasks && officePartnerData.data.tasks.assigned && officePartnerData.data.tasks.assigned.products) {
        console.log('UserRoleLayout - Found products in data.tasks.assigned.products:', officePartnerData.data.tasks.assigned.products);
        return Array.isArray(officePartnerData.data.tasks.assigned.products) ? officePartnerData.data.tasks.assigned.products : [];
      }
    }
    // Check if it's the assigned object directly (products at same level as inquiry/tasks)
    if (officePartnerData.products !== undefined) {
      console.log('UserRoleLayout - Found products in officePartnerData.products:', officePartnerData.products);
      return Array.isArray(officePartnerData.products) ? officePartnerData.products : [];
    }
    
    console.log('UserRoleLayout - No products found in officePartnerData');
    return [];
  }, [officePartnerData]);

  // Extract partners from officePartnerData (similar to products)
  const partnersData = useMemo(() => {
    if (!officePartnerData) return [];
    
    // Check in data.tasks.assigned.partners
    if (officePartnerData.data && officePartnerData.data.tasks && officePartnerData.data.tasks.assigned && officePartnerData.data.tasks.assigned.partners) {
      console.log('UserRoleLayout - Found partners in data.tasks.assigned.partners:', officePartnerData.data.tasks.assigned.partners);
      return Array.isArray(officePartnerData.data.tasks.assigned.partners) ? officePartnerData.data.tasks.assigned.partners : [];
    }
    // Check if it's the assigned object directly (partners at same level as inquiry/tasks)
    if (officePartnerData.partners !== undefined) {
      console.log('UserRoleLayout - Found partners in officePartnerData.partners:', officePartnerData.partners);
      return Array.isArray(officePartnerData.partners) ? officePartnerData.partners : [];
    }
    
    console.log('UserRoleLayout - No partners found in officePartnerData');
    return [];
  }, [officePartnerData]);
  
  console.log('UserRoleLayout - Final productsData:', productsData);

  // Extract section options from categories.product_line.section
  const sectionOptionsData = useMemo(() => {
    if (!officePartnerData) {
      console.log('UserRoleLayout - officePartnerData is null/undefined for section options');
      return [];
    }

    console.log('UserRoleLayout - officePartnerData structure for section options:', officePartnerData);

    // Helper function to extract section from categories
    const extractSectionFromCategories = (data) => {
      // Check if it's the full API response structure
      if (data.data) {
        // Check in data.categories.product_line.section
        if (data.data.categories && data.data.categories.product_line && data.data.categories.product_line.section) {
          console.log('UserRoleLayout - Found section in data.categories.product_line.section:', data.data.categories.product_line.section);
          return Array.isArray(data.data.categories.product_line.section) ? data.data.categories.product_line.section : [];
        }
        // Check in data.tasks.assigned.categories.product_line.section
        if (data.data.tasks && data.data.tasks.assigned && data.data.tasks.assigned.categories && 
            data.data.tasks.assigned.categories.product_line && data.data.tasks.assigned.categories.product_line.section) {
          console.log('UserRoleLayout - Found section in data.tasks.assigned.categories.product_line.section:', data.data.tasks.assigned.categories.product_line.section);
          return Array.isArray(data.data.tasks.assigned.categories.product_line.section) ? data.data.tasks.assigned.categories.product_line.section : [];
        }
        // Fallback: Check in data.section_options (for backward compatibility)
        if (data.data.section_options) {
          console.log('UserRoleLayout - Found section_options in data.section_options (fallback):', data.data.section_options);
          return Array.isArray(data.data.section_options) ? data.data.section_options : [];
        }
      }
      // Check if it's the assigned object directly
      if (data.categories && data.categories.product_line && data.categories.product_line.section) {
        console.log('UserRoleLayout - Found section in categories.product_line.section:', data.categories.product_line.section);
        return Array.isArray(data.categories.product_line.section) ? data.categories.product_line.section : [];
      }
      // Fallback: Check section_options (for backward compatibility)
      if (data.section_options !== undefined) {
        console.log('UserRoleLayout - Found section_options (fallback):', data.section_options);
        return Array.isArray(data.section_options) ? data.section_options : [];
      }
      return null;
    };

    const sectionData = extractSectionFromCategories(officePartnerData);
    
    if (sectionData) {
      return sectionData;
    }
    
    console.log('UserRoleLayout - No section options found in officePartnerData');
    return [];
  }, [officePartnerData]);
  
  console.log('UserRoleLayout - Final sectionOptionsData:', sectionOptionsData);

  // Extract categories data from API response
  // API structure: data.tasks.assigned.categories = { tasks: {...}, inquiry: {...}, product_line: {...} }
  const categoriesData = useMemo(() => {
    if (!officePartnerData) {
      console.log('UserRoleLayout - officePartnerData is null/undefined for categories');
      return {};
    }

    console.log('UserRoleLayout - officePartnerData structure for categories:', officePartnerData);

    // Helper function to extract categories
    const extractCategories = (data) => {
      // Check if it's the full API response structure: { status: 200, data: { tasks: { assigned: { categories: {...} } } } }
      if (data.data && data.data.tasks && data.data.tasks.assigned && data.data.tasks.assigned.categories) {
        console.log('UserRoleLayout - Found categories in data.tasks.assigned.categories:', data.data.tasks.assigned.categories);
        return data.data.tasks.assigned.categories;
      }
      // Check if it's the assigned object directly: { categories: {...} }
      if (data.categories) {
        console.log('UserRoleLayout - Found categories in officePartnerData.categories:', data.categories);
        return data.categories;
      }
      // Check if it's tasks.assigned.categories directly
      if (data.tasks && data.tasks.assigned && data.tasks.assigned.categories) {
        console.log('UserRoleLayout - Found categories in tasks.assigned.categories:', data.tasks.assigned.categories);
        return data.tasks.assigned.categories;
      }
      return null;
    };

    const categories = extractCategories(officePartnerData);
    
    if (categories) {
      return categories;
    }
    
    console.log('UserRoleLayout - No categories found in officePartnerData');
    return {};
  }, [officePartnerData]);
  
  console.log('UserRoleLayout - Final categoriesData:', categoriesData);

  // Transform tasks for DashboardTable calendar view
  // DashboardTable expects: array of user objects with { userId, firstname, lastname, tasks: [...] }
  // Each task should have: { id, startDate, endDate, taskName, country_side_color, countryside, ... }
  const dashboardTableData = useMemo(() => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      return null;
    }

    // Transform UserRole tasks to DashboardTable format
    const transformedTasks = tableData.map((item) => {
      // Parse dates - handle both string and Date formats
      const startDate = item.startDate || item.executionDate;
      const endDate = item.endDate || item.deadlineDate;
      
      // Handle countryside - extract value if it's an object, otherwise use as string
      let countrysideValue = '';
      if (item.countryside) {
        if (typeof item.countryside === 'object' && item.countryside.value !== undefined) {
          countrysideValue = item.countryside.value;
        } else if (typeof item.countryside === 'string') {
          countrysideValue = item.countryside;
        }
      }
      
      return {
        id: item.id,
        item_id: item.item_id || item.id,
        taskName: item.title || item.taskName || '',
        assignedBy: item.assignedBy || '',
        startDate: startDate ? (typeof startDate === 'string' ? new Date(startDate) : startDate) : new Date(),
        endDate: endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : new Date(),
        country_side_color: item.country_side_color || '#3498db',
        countryside: countrysideValue,
        invoice_description: item.invoice_description || '',
        customer_address: item.customer_address || '',
        customer_phone_number: item.customer_phone_number || '',
        price: item.price || 0,
        type: item.type || '',
        status: item.status || '',
        ...item, // Include all other properties
        countryside: countrysideValue // Override countryside with string value
      };
    });

    // Create a single user object with all tasks (since UserRole view shows tasks for current user)
    // If tasks have user information, we could group them, but for now create a single user
    return [{
      userId: 1, // Default user ID for UserRole view
      firstname: 'My',
      lastname: 'Tasks',
      tasks: transformedTasks
    }];
  }, [tableData]);

  // Calculate filters (startDate, endDate) and columnWidth for DashboardTable
  // Use filters from context (set by DashboardFilters) if available, otherwise calculate from data
  const dashboardFilters = useMemo(() => {
    // First, check if filters from context are available (set by DashboardFilters component)
    if (filters?.startDate && filters?.endDate) {
      return {
        startDate: filters.startDate instanceof Date ? filters.startDate : new Date(filters.startDate),
        endDate: filters.endDate instanceof Date ? filters.endDate : new Date(filters.endDate)
      };
    }

    // Fallback: Calculate from data if no filters in context
    if (!dashboardTableData || !dashboardTableData[0]?.tasks?.length) {
      // Default date range if no tasks
      const defaultStartDate = startOfISOWeek(subWeeks(new Date(), 5));
      const defaultEndDate = addDays(new Date(), 90);
      return {
        startDate: defaultStartDate,
        endDate: defaultEndDate
      };
    }

    // Find min and max dates from all tasks
    const allDates = dashboardTableData[0].tasks.flatMap(task => [
      task.startDate ? new Date(task.startDate) : null,
      task.endDate ? new Date(task.endDate) : null
    ]).filter(Boolean);

    if (allDates.length === 0) {
      const defaultStartDate = startOfISOWeek(subWeeks(new Date(), 5));
      const defaultEndDate = addDays(new Date(), 90);
      return {
        startDate: defaultStartDate,
        endDate: defaultEndDate
      };
    }

    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    
    // Add some buffer (5 weeks before, 90 days after)
    const startDate = startOfISOWeek(subWeeks(minDate, 5));
    const endDate = addDays(maxDate, 90);

    return {
      startDate,
      endDate
    };
  }, [dashboardTableData, filters?.startDate, filters?.endDate]);

  // Calculate columnWidth for DashboardTable
  const dashboardColumnWidth = useMemo(() => {
    if (!dashboardFilters.startDate || !dashboardFilters.endDate) {
      return 50; // Default width
    }

    const minWidth = 50;
    const days = differenceInDays(dashboardFilters.endDate, dashboardFilters.startDate);
    const viewWidth = window.innerWidth - 116;
    const calendarWidth = viewWidth;
    const colWidth = calendarWidth / (days + 1);
    return colWidth < minWidth ? minWidth : colWidth;
  }, [dashboardFilters]);

  // Generate dates array for DashboardTable
  const dashboardDates = useMemo(() => {
    if (!dashboardFilters.startDate || !dashboardFilters.endDate) {
      return [];
    }

    let dates = [];
    for (
      let date = new Date(dashboardFilters.startDate);
      date <= dashboardFilters.endDate;
      date.setDate(date.getDate() + 1)
    ) {
      const formattedDate = {
        date: date.toDateString(),
        dayName: getDayName(date),
        dayNumber: date.getDate(),
        monthName: getMonthName(date),
      };
      dates.push(formattedDate);
    }
    return dates;
  }, [dashboardFilters]);

  const handleViewChange = (viewType) => {
    setSelectedView(viewType);
    // Reset calendar state when switching away from task view
    if (viewType !== 'task') {
      setIsCalendarClicked(false);
    }
    // Reset selected row when switching views
    setSelectedRow(null);
    setIsPopupOpen(false);
    
    // Clear all filters when switching between Task and Enquiry (like initial fetch)
    // But preserve service_managers filter value (don't reset it to null)
    const today = new Date();
    const date = today.toISOString().split("T")[0];
    const currentServiceManagers = filters?.service_managers || null;
    
    dispatch({
      type: "SET_FILTERS",
      payload: {
        type: null,
        service_managers: null, // Preserve service_managers filter
        partner: null,
        lansdel: null,
        inquiry_status: null,
        task_status: null,
        date: date,
        startDate: startOfISOWeek(subWeeks(new Date(), 5)),
        endDate: addDays(new Date(), 90),
        filter: "",
        getSelectedYear: null,
        getSelectedMonth: null,
        getSelectedWeekNumbers: 0,
        selectedFilter: null,
      },
    });
    
    console.log('Selected view in UserRole component:', viewType);
  };

  const handleCalendarClick = (isClicked) => {
    setIsCalendarClicked(isClicked);
    console.log('Calendar clicked in UserRole component:', isClicked);
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedRow(null);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClosePopup();
    }
  };

  const handleProductLineClick = () => {
    setIsPopupOpen(false); // Close the detail popup
    setIsProductLinePopupOpen(true); // Open the product line popup
  };

  const handleCloseProductLinePopup = () => {
    setIsProductLinePopupOpen(false);
  };

  const handleProductLineOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseProductLinePopup();
    }
  };

  // Handle product line update - refresh data after product line is created/updated
  const handleProductLineUpdate = async () => {
    // Refresh the dashboard data to get updated product lines
    if (fetchData) {
      await fetchData();
      // After data refreshes, update selectedRow if it exists to reflect new product lines
      // This ensures the detail popup shows updated product lines when reopened
      if (selectedRow && officePartnerData) {
        // Find the updated row in the refreshed data
        const updatedData = officePartnerData.data?.tasks?.assigned || officePartnerData;
        const allTasks = updatedData.tasks || [];
        const allInquiries = updatedData.inquiry || [];
        const allItems = [...allTasks, ...allInquiries];
        const updatedRow = allItems.find(item => 
          (item.item_id || item.id) === (selectedRow.item_id || selectedRow.id)
        );
        if (updatedRow) {
          setSelectedRow(updatedRow);
        }
      }
    }
  };

  // Handle task/inquiry update - refresh data after task or inquiry is updated
  const handleTaskInquiryUpdate = async (updatedDataFromAPI) => {
    // Update selectedRow immediately with API response data for instant UI update
    if (updatedDataFromAPI && selectedRow) {
      // Merge the updated data with the existing selectedRow
      // This ensures we preserve all fields and only update the changed ones
      const mergedRow = {
        ...selectedRow,
        ...updatedDataFromAPI,
        // Preserve ProductLine array if it exists
        ProductLine: selectedRow.ProductLine || updatedDataFromAPI.ProductLine,
        productLine: selectedRow.productLine || updatedDataFromAPI.productLine,
      };
      setSelectedRow(mergedRow);
    }

    // Refresh the dashboard data to get fully synced data from server
    if (fetchData) {
      await fetchData();
      // After data refreshes, update selectedRow if it exists to reflect fully synced data
      if (selectedRow && officePartnerData) {
        // Find the updated row in the refreshed data
        const updatedData = officePartnerData.data?.tasks?.assigned || officePartnerData;
        const allTasks = updatedData.tasks || [];
        const allInquiries = updatedData.inquiry || [];
        const allItems = [...allTasks, ...allInquiries];
        const updatedRow = allItems.find(item => 
          (item.item_id || item.id) === (selectedRow.item_id || selectedRow.id)
        );
        if (updatedRow) {
          setSelectedRow(updatedRow);
        }
      }
    }
  };

  // Handle task click from calendar view - transform task data to match selectedRow format
  const handleTaskClick = (task, user) => {
    // Format dates - convert Date objects to strings if needed
    const formatDate = (date) => {
      if (!date) return '';
      if (date instanceof Date) {
        return date.toLocaleDateString();
      }
      return date;
    };

    // Transform task data to match UserRoleDetailPopup expected format
    const rowData = {
      id: task.id,
      title: task.taskName || task.title || '',
      assignedBy: task.assignedBy || '',
      executionDate: formatDate(task.startDate || task.executionDate),
      deadlineDate: formatDate(task.endDate || task.deadlineDate),
      ...task // Include all other properties from the task
    };
    setSelectedRow(rowData);
    setIsPopupOpen(true);
  };

  return (
    <>
     <UserRoleTopHeader />
      <DashboardFilters 
        showTypeFilter={true}
        showServiceManagerFilter={true}
        showPartnerFilter={false}
        showLansdelFilter={true}
        showDateFilter={true}
        showProfile={false}
        selectedView={selectedView}
      /> 
      <section className="user-role">
      <div className="container">
      <UserRoleTableView 
        onViewChange={handleViewChange} 
        selectedView={selectedView} 
        onCalendarClick={handleCalendarClick} 
      />
      {selectedView === 'task' && isCalendarClicked ? (
        <DashboardTable 
          data={dashboardTableData}
          filters={dashboardFilters}
          columnWidth={dashboardColumnWidth}
          dates={dashboardDates}
          unassignedData={null}
          hideUnassigned={true}
          hideTimeline={true}
          onTaskClick={handleTaskClick}
          hideMainTaskView={true}
          calendarHeightIncrease={500}
          hideUserName={true}
          hideCountryside={true}
        />
      ) : (
        <UserRoleTable UserRoleData={tableData} onRowClick={handleRowClick} selectedRow={selectedRow} />
      )}
      </div>
      </section>

      <UserRoleDetailPopup 
        isOpen={isPopupOpen}
        selectedRow={selectedRow}
        rowType={selectedView === 'enquiry' ? 'enquiry' : 'task'}
        onClose={handleClosePopup}
        onOverlayClick={handleOverlayClick}
        onProductLineClick={handleProductLineClick}
        categories={categoriesData}
        products={productsData}
        partners={partnersData}
        onUpdate={handleTaskInquiryUpdate}
      />

      <ProductLinePopup 
        isOpen={isProductLinePopupOpen}
        selectedRow={selectedRow}
        onClose={handleCloseProductLinePopup}
        onOverlayClick={handleProductLineOverlayClick}
        products={productsData}
        sectionOptions={sectionOptionsData}
        onUpdate={handleProductLineUpdate}
        rowType={selectedView === 'enquiry' ? 'enquiry' : 'task'}
      />
    </>
  )
}
export default UserRoleLayout;